import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import {
  type AppointmentDurationBehaviorKey,
  parseDurationMapFromJson,
} from "@/lib/appointments/default-duration";
import { useProfile } from "@/hooks/useProfile";

export type ClinicDefaultAppointmentDurationsState = {
  clinicId: string | null;
  minutesByType: Partial<Record<AppointmentDurationBehaviorKey, number>>;
};

export const useClinicDefaultAppointmentDurations = () => {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: ["clinic-default-appointment-durations", clinicId],
    queryFn: async (): Promise<ClinicDefaultAppointmentDurationsState> => {
      if (!clinicId) {
        return { clinicId: null, minutesByType: {} };
      }

      const { data, error } = await supabase
        .from("clinics")
        .select("id, default_appointment_durations_minutes")
        .eq("id", clinicId)
        .maybeSingle();

      if (error) throw error;

      return {
        clinicId,
        minutesByType: parseDurationMapFromJson(data?.default_appointment_durations_minutes ?? null),
      };
    },
    enabled: profile !== undefined,
  });
};

export const useSetClinicDefaultAppointmentDurations = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (minutesByType: Record<AppointmentDurationBehaviorKey, number>): Promise<Json> => {
      if (!clinicId) {
        throw new Error("clinic_required");
      }

      const p_durations: Record<string, number> = {};
      for (const [key, value] of Object.entries(minutesByType) as Array<
        [AppointmentDurationBehaviorKey, number]
      >) {
        p_durations[key] = value;
      }

      const { data, error } = await supabase.rpc("set_clinic_default_appointment_durations", {
        p_clinic_id: clinicId,
        p_durations: p_durations as unknown as Json,
      });

      if (error) throw error;
      return data as Json;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["clinic-default-appointment-durations", clinicId] });
    },
  });
};
